import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Mail } from "lucide-react";
import GitPrompt from "@/components/GitPrompt";
import { getAllPosts, BlogPost } from "../utils/markdown";
import { siteConfig } from "@/data/site";
import { useTimeTheme } from "@/hooks/use-time-theme";

export const Blog: React.FC = () => {
  const theme = useTimeTheme();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [categories, setCategories] = useState<{ id: string; label: string }[]>([
    { id: "all", label: "All Posts" },
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const allPosts = await getAllPosts();
        
        if (isMounted) {
          if (!allPosts || allPosts.length === 0) {
            setError("No blog posts found yet.");
          } else {
            setPosts(allPosts);
            const unique = Array.from(new Set(allPosts.map((p) => p.category))).filter(Boolean);
            const derived = [
              { id: "all", label: "All Posts" },
              ...unique.map((c) => ({ id: c, label: c })),
            ];
            setCategories(derived);
            const validIds = new Set(derived.map((c) => c.id));
            if (!validIds.has(activeCategory)) {
              setActiveCategory("all");
            }
          }
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to load blog posts. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredPosts =
    activeCategory === "all"
    ? posts
      : posts.filter((post) => post.category === activeCategory);

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text} transition-colors duration-1000`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Matching home page style */}
        <header className="pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link
              to="/"
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${theme.accent} hover:opacity-80 transition-all text-center sm:text-left`}
            >
              {siteConfig.name.split(" ")[0].toLowerCase()}.space
            </Link>
            
            {/* Navigation - Centered on mobile, right-aligned on large screens */}
            <nav className={`flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6 text-sm sm:text-base ${theme.accent}/80`}>
              <Link to="/about" className="hover:opacity-100 transition-colors">
                About
              </Link>
              <Link to="/blog" className="hover:opacity-100 transition-colors">
                Blog
              </Link>
              <Link to="/projects" className="hover:opacity-100 transition-colors">
                Projects
              </Link>
              <Link to="/uses" className="hover:opacity-100 transition-colors">
                Uses
              </Link>
              <Link to="/opensource" className="hover:opacity-100 transition-colors">
                Open Source
              </Link>
            </nav>
                </div>
        </header>

        {/* Main Content */}
        <main className="pb-12 sm:pb-16">
          <section className="space-y-8 sm:space-y-10">
            {/* Git Prompt */}
            <GitPrompt command='git log --author="diti"' className="justify-center sm:justify-start" />
            
            {/* Title and Description */}
            <div className="space-y-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-white">
                Notes and reflections.
              </h1>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                Practical writing on systems, security, and building tools people love to use.
              </p>
          </div>

            {/* Category Filters */}
            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2 sm:gap-3">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                    className={`px-3 py-1.5 text-xs sm:text-sm rounded transition-all ${
                          activeCategory === category.id
                        ? "text-cyan-400 bg-cyan-400/10 font-medium"
                        : "text-slate-400 hover:text-slate-200 border border-slate-700/30"
                        }`}
                      >
                        {category.label}
                      </button>
                    ))}
                  </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="p-6 rounded-lg border border-slate-700/30 text-slate-400 text-sm">
                Loading posts...
              </div>
            )}

          {/* Error State */}
            {error && !loading && (
              <div className="p-6 rounded-lg border border-slate-700/30 text-slate-400 text-sm">
                {error}
            </div>
          )}

            {/* Posts List */}
            {!loading && !error && (
              <div className="space-y-4 sm:space-y-6">
                {filteredPosts.length === 0 ? (
                  <div className="p-6 rounded-lg border border-slate-700/30 text-slate-400 text-sm">
                    No posts in this category.
                  </div>
                ) : (
                  filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                      className="block p-4 sm:p-6 rounded-lg border border-slate-700/30 hover:border-cyan-500/50 transition-all group text-slate-200 hover:text-white"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <span className="text-xs sm:text-sm text-slate-400">
                          {new Date(post.date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                      </span>
                        {post.category && (
                          <span className="text-xs px-2 py-1 rounded bg-cyan-400/20 text-cyan-400 inline-block w-fit">
                        {post.category}
                          </span>
                        )}
                    </div>
                      <h2 className="text-lg sm:text-xl font-semibold mb-2 group-hover:text-cyan-400 transition-colors">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                          {post.excerpt}
                        </p>
                      )}
                </Link>
                  ))
          )}
              </div>
            )}
          </section>

          {/* Social Links and Footer - Matching home page style */}
          <section className="mt-16 sm:mt-20 md:mt-24 flex flex-col items-center gap-6 text-slate-400">
            <div className="flex items-center gap-6">
              <a
                href={siteConfig.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="transition-colors hover:opacity-100"
              >
                <Github className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={siteConfig.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="transition-colors hover:opacity-100"
              >
                <Linkedin className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                aria-label="Email"
                className="transition-colors hover:opacity-100"
              >
                <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
            </div>
            <p className="text-xs sm:text-sm flex flex-wrap items-center justify-center gap-2">
              <span>&gt; built with</span>
              <span className="inline-flex h-4 overflow-hidden align-middle">
                <span className="rotate-words">
                  <span>Cursor</span>
                  <span>love</span>
                  <span>coffee</span>
                  <span>code</span>
                </span>
              </span>
            </p>
          </section>
        </main>
        </div>
    </div>
  );
};
